import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { LiliButton } from '@lili/lili-frontend-common';
import { FORGOT_PASSWORD_FLOW, GENERAL_STYLE } from '../constants';
import LoginCard from '../../../components/LoginCard';
import Heading from '../../../../../generic-components/Heading';
import Button from '../../../../../generic-components/Button';
import { LoginHelpNavBar } from '../LoginHelpNavBar';
import DigitsContainer from '../../../../../generic-components/DigitsContainer';
import { usePasswordRecoveryChallenge } from '../ForgotEmail/hooks/forgotPasswordHooks';
import { MFA_METHOD } from './ForgotPasswordMFMethodStep';
import { getClientId } from '../../../../../helpers';
import { ForgotPasswordGeneralErrorModal } from '../Errors/ForgotPasswordGeneralErrorModal';
import { ReactComponent as InlineError } from '../../../../../assets/svg/inline-error.svg';
import Spinner, { SpinnerColor } from '../../../../../generic-components/Spinner';
import { SERVER_ERRORS_DATA } from '../../../../../constants/serverErrors';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/svg/stamp-icons/ic-stamp-timewatch.svg';
import { Layout } from '../../../../../components/Layout/Layout';

interface IProps {
  challenge: any
  mfaType: string
  timeLeftToResend: number | null
  didResendCode: boolean
  isLoading: boolean
  onClose: () => void
  onBack: () => void
  onResend: () => void
  onCodeVerified: (token: any) => void
}
const ForgotPasswordCodeVerificationStep = ({
  challenge, mfaType, onClose, onBack, onResend, onCodeVerified, timeLeftToResend, didResendCode, isLoading,
}: IProps) => {
  const { t } = useTranslation();
  const [codeInput, setCodeInput] = useState<string>('');
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [isMFACodeError, setIsMFACodeError] = useState<boolean>(false);
  const [showUserLocked, setShowUserLocked] = useState<boolean>(false);

  const numberOfDigits = () => {
    switch (mfaType) {
      case MFA_METHOD.SMS: return 6;
      case MFA_METHOD.VOICE: return 5;
      default: return 5;
    }
  };

  const { mutateAsync: mutatePasswordRecoveryChallenge, isLoading: isAnsweringChallenge } = usePasswordRecoveryChallenge(
    challenge.customerId,
    (code) => {
      switch (code) {
        case SERVER_ERRORS_DATA.AUTH_TOKEN_INVALID:
        case SERVER_ERRORS_DATA.AUTH_TOKEN_EXPIRED:
        case SERVER_ERRORS_DATA.INVALID_ANSWER:
        case SERVER_ERRORS_DATA.MFA_CODE_CAN_BE_USED_ONCE:
          setIsMFACodeError(true);
          setShowErrorModal(false);
          setShowUserLocked(false);
          break;
        case SERVER_ERRORS_DATA.USER_LOCKED_CODE:
          setIsMFACodeError(false);
          setShowErrorModal(false);
          setShowUserLocked(true);
          break;
        default:
          setIsMFACodeError(false);
          setShowErrorModal(true);
          setShowUserLocked(false);
          break;
      }
    },
  );
  const answerChallenge = (answeredChallenge: any) => {
    setIsMFACodeError(false);
    answeredChallenge.answer = codeInput;
    answeredChallenge.clientId = getClientId();
    answeredChallenge.rememberMe = true;
    mutatePasswordRecoveryChallenge(answeredChallenge)
      .then((response1) => {
        onCodeVerified(response1.token);
      });
  };
  const heading = (
    <Heading
      title={t(FORGOT_PASSWORD_FLOW.CODE_VERIFICATION.TITLE)}
      className={`${GENERAL_STYLE.HEADING.CLASS_NAME} mb-[16px]`}
    />
  );
  const subtitle = (
    <Heading
      title={t(FORGOT_PASSWORD_FLOW.CODE_VERIFICATION.SUBTITLE).replace('{PHONE}', challenge.mfaDestination)}
      className={`${GENERAL_STYLE.DESCRIPTION.CLASS_NAME} mb-[40px]`}
    />
  );
  const countdownText = (
    <div className='flex w-full justify-center items-center mb-[68px]'>
      <span className='font-metricRMedium text-[14px] text-bw07'>
        {t(FORGOT_PASSWORD_FLOW.CODE_VERIFICATION.RESEND_COUNT_TEXT)}
        {' '}
        <span className='font-metricRBold text-[14px] text-bw07'>
          {timeLeftToResend}
        </span>
        {t(FORGOT_PASSWORD_FLOW.CODE_VERIFICATION.RESEND_COUNT_SECONDS)}
      </span>
    </div>

  );
  const resendButton = (
    <div className='flex mb-[68px] justify-center items-center'>

      {didResendCode && <Heading title={t(FORGOT_PASSWORD_FLOW.CODE_VERIFICATION.DID_NOT_GET_CODE)} className='font-metricRMedium text-[14px] text-bw07 mr-[4px]' />}

      <Button
        id='resendButton'
        title={t(FORGOT_PASSWORD_FLOW.CODE_VERIFICATION.RESEND_BUTTON_TITLE)}
        className={`${GENERAL_STYLE.SECONDARY_BUTTON.CLASS_NAME}`}
        onClick={() => {
          onResend();
        }}
      />
    </div>

  );

  const mainButton = (
    <LiliButton
      id='mainButton'
      title={t(FORGOT_PASSWORD_FLOW.CODE_VERIFICATION.CONTINUE_BUTTON_TITLE)}
      className={`${GENERAL_STYLE.MAIN_BUTTON.CLASS_NAME}`}
      onClick={() => {
        answerChallenge(challenge);
      }}
      disabled={isButtonDisabled}
      isLoading={isAnsweringChallenge}
    />
  );

  const errorModal = (

    showErrorModal && (
      <ForgotPasswordGeneralErrorModal
        title={t(FORGOT_PASSWORD_FLOW.ERRORS.GENERAL_ERROR_TITLE)}
        description={t(FORGOT_PASSWORD_FLOW.ERRORS.GENERAL_ERROR_DESCRIPTION)}
        onAction={() => {
          setShowErrorModal(false);
          answerChallenge(challenge);
        }}
        onClose={() => { setShowErrorModal(false); }}
      />
    )

  );
  const userLockedError = (

    showUserLocked && (
      <ForgotPasswordGeneralErrorModal
        title={t(FORGOT_PASSWORD_FLOW.ERRORS.USER_LOCKED_ERROR_TITLE)}
        description={t(FORGOT_PASSWORD_FLOW.ERRORS.USER_LOCKED_ERROR_DESCRIPTION)}
        mainButtonTitle={t(FORGOT_PASSWORD_FLOW.ERRORS.ERROR_BUTTON_GOT_IT)}
        onAction={() => {
          setShowErrorModal(false);
          setShowUserLocked(false);
        }}
        hideSecondaryButton
        icon={<ClockIcon />}
        onClose={() => setShowUserLocked(false)}
      />
    )

  );
  const inlineError = (

    <div className={`flex flex-row w-full place-content-center  mt-[12px] opacity-${!isMFACodeError ? '0' : '1'}`}>
      <InlineError className='mt-[4px] mr-[4px]' />
      <Heading
        title={t(FORGOT_PASSWORD_FLOW.ERRORS.INVALID_CODE)}
        className={`${GENERAL_STYLE.DESCRIPTION.CLASS_NAME} text-red02`}
      />
    </div>

  );
  const spinner = (<Spinner containerClassName='mb-[68px]' spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />);
  const digitsContainer = (
    <DigitsContainer
      containerClassName='flex space-x-[8px] justify-center'
      numOfDigits={numberOfDigits()}
      onFulfill={(ssn) => {
        setCodeInput(ssn);
        setIsButtonDisabled(ssn.length !== numberOfDigits());
      }}
      onDigitsChanged={(ssn) => {
        setIsMFACodeError(false);
        setCodeInput(ssn);
        setIsButtonDisabled(ssn.length !== numberOfDigits());
      }}
      isLoading={isAnsweringChallenge}
      isError={isMFACodeError}
    />
  );
  return (
    <Layout shouldHideProfile shouldHideChatBot wrapperClassName='pt-[78px] pl-[72px] ml-[100px] pc-md:ml-[256px]'>
      <LoginHelpNavBar
        onBackClick={onBack}
        onCloseClick={onClose}
      />

      <LoginCard className={GENERAL_STYLE.LOGIN_CARD.CLASS_NAME}>
        <div className='flex flex-col justify-between h-full '>

          <div className='mb-[49px]'>
            {heading}
            {subtitle}
            {userLockedError}
            {errorModal}
            {!isLoading && digitsContainer}
            {!isLoading && inlineError}
          </div>
          {isLoading ? spinner : (timeLeftToResend !== null ? countdownText : resendButton)}
          {mainButton}
        </div>
      </LoginCard>
    </Layout>

  );
};

export default ForgotPasswordCodeVerificationStep;
